// ============================================================================
// NetworkGraph — SoulSearch Phase B (MVP D3.js force-directed)
// ============================================================================
// Graph sederhana: subject di tengah, platforms sebagai mid-ring nodes,
// handles sebagai outer nodes. Edge thickness = confidence.
// Lazy-load D3 untuk hemat bundle size.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../../contexts/AppContext';

// Canvas tidak resolve CSS var, jadi siapkan palette per tema.
// Per CLAUDE.md: ctx.fillStyle/strokeStyle WAJIB hex literal di canvas.
const PALETTES = {
  dark: {
    cyan:   '#00d4ff', green:  '#00ff88', red:    '#ff2d55',
    orange: '#ff6b2b', yellow: '#ffd60a', purple: '#7c4dff',
    muted:  '#444',
  },
  light: {
    cyan:   '#0084b8', green:  '#008844', red:    '#d12440',
    orange: '#c54a00', yellow: '#b89400', purple: '#5a35d4',
    muted:  '#94a3b8',
  },
};

function getPlatformColors(p) {
  return {
    instagram: p.orange, facebook: p.cyan,   linkedin: p.purple,
    x:         p.red,    twitter:  p.red,    tiktok:   p.red,
    youtube:   p.red,    web:      p.cyan,   news:     p.yellow, court: p.yellow,
  };
}

/**
 * NetworkGraph MVP — canvas-based fallback kalau D3 tidak di-load.
 * Untuk demo: simple static graph dengan animasi CSS.
 *
 * @param {object} props
 * @param {string} props.subjectName  Nama subject di tengah
 * @param {Array}  props.edges        [{source, target, platform, weight}]
 * @param {string} props.lang
 */
export default function NetworkGraph({ subjectName, edges = [], lang = 'id' }) {
  const canvasRef = useRef(null);
  const [d3Loaded, setD3Loaded] = useState(false);
  const { theme } = useApp();
  const palette = PALETTES[theme === 'light' ? 'light' : 'dark'];
  const PLATFORM_COLORS = getPlatformColors(palette);

  // Group edges by platform → mid-ring nodes
  const platforms = [...new Set(edges.map(e => e.platform || 'web'))];
  const handles   = edges.slice(0, 12); // cap untuk MVP

  useEffect(() => {
    // Lazy-load D3 jika diperlukan untuk advanced rendering
    // Untuk MVP cukup dengan canvas sederhana
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    const cx = w / 2, cy = h / 2;

    ctx.clearRect(0, 0, w, h);

    // Subject node di tengah
    ctx.beginPath();
    ctx.arc(cx, cy, 18, 0, Math.PI * 2);
    ctx.fillStyle = palette.cyan + '22';
    ctx.fill();
    ctx.strokeStyle = palette.cyan;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = palette.cyan;
    ctx.font = 'bold 8px IBM Plex Mono';
    ctx.textAlign = 'center';
    ctx.fillText('SUBJECT', cx, cy + 3);

    if (edges.length === 0) {
      ctx.fillStyle = palette.muted;
      ctx.font = '10px IBM Plex Mono';
      ctx.fillText(lang === 'id' ? 'Belum ada data' : 'No data yet', cx, cy + 35);
      return;
    }

    // Platform nodes dalam circle
    const platformList = platforms.slice(0, 8);
    platformList.forEach((platform, i) => {
      const angle = (i / platformList.length) * Math.PI * 2 - Math.PI / 2;
      const px = cx + 65 * Math.cos(angle);
      const py = cy + 55 * Math.sin(angle);
      const color = PLATFORM_COLORS[platform] || palette.cyan;

      // Edge ke subject
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(px, py);
      ctx.strokeStyle = color + '44';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Platform node
      ctx.beginPath();
      ctx.arc(px, py, 10, 0, Math.PI * 2);
      ctx.fillStyle = color + '22';
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.fillStyle = color;
      ctx.font = '7px IBM Plex Mono';
      ctx.textAlign = 'center';
      ctx.fillText(platform.slice(0, 3).toUpperCase(), px, py + 2.5);

      // Handles untuk platform ini
      const platformEdges = edges.filter(e => e.platform === platform).slice(0, 3);
      platformEdges.forEach((edge, j) => {
        const subAngle = angle + (j - 1) * 0.3;
        const hx = cx + 105 * Math.cos(subAngle);
        const hy = cy + 90 * Math.sin(subAngle);

        // Edge platform → handle
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(hx, hy);
        ctx.strokeStyle = color + '33';
        ctx.lineWidth = 0.8;
        ctx.stroke();

        // Handle node
        ctx.beginPath();
        ctx.arc(hx, hy, 5, 0, Math.PI * 2);
        ctx.fillStyle = color + '44';
        ctx.fill();
        ctx.strokeStyle = color + '88';
        ctx.lineWidth = 1;
        ctx.stroke();
      });
    });
  }, [edges, platforms, handles, lang, theme]);

  return (
    <div style={{ position: 'relative' }}>
      <p style={{ fontSize: 8, fontFamily: 'IBM Plex Mono', color: 'var(--text-mute)',
                  textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>
        {lang === 'id' ? 'Peta Koneksi Digital' : 'Digital Network Map'}
        {' '}
        <span style={{ color: 'var(--text-mute)' }}>({edges.length} koneksi)</span>
      </p>
      <canvas
        ref={canvasRef}
        width={260}
        height={220}
        style={{
          background: 'var(--color-card)',
          borderRadius: 10,
          border: '1px solid var(--border-dim)',
          display: 'block',
        }}
      />
      {edges.length === 0 && (
        <div style={{
          position: 'absolute', inset: 28, display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: 'var(--text-mute)',
          fontSize: 10, fontFamily: 'IBM Plex Mono',
        }}>
          {lang === 'id' ? 'Belum ada data koneksi' : 'No connection data yet'}
        </div>
      )}
    </div>
  );
}
